import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { moodleCourseProgressText } from '../../../helpers/moodle-progress';
import { environment } from '../../../../environments/environment';

interface LearnerProfileData {
  profile: any;
  observations: any[];
  consolidated_summary: any;
  learning_progress: any;
}

interface ProfileEntry {
  key: string;
  value: any;
}

interface ObservationRow {
  index: number;
  observation: any;
}

@Component({
  selector: 'app-learner-profile-dialog',
  templateUrl: './learner-profile-dialog.component.html',
  styleUrl: './learner-profile-dialog.component.scss',
})
export class LearnerProfileDialogComponent implements OnInit {
  @Input() learnerId!: string;
  @Input() learnerName = '';
  @Input() learnerEmail = '';

  isLoading = true;
  data: LearnerProfileData | null = null;
  expandedObservation: number | null = null;
  observationFilter = '';
  moodleCourseFilter = '';

  constructor(
    protected dialogRef: NbDialogRef<LearnerProfileDialogComponent>,
    private http: HttpClient,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.http
      .get<{ data: LearnerProfileData }>(
        `${environment.tutorsBackend}/admin/learners/${this.learnerId}/profile`
      )
      .subscribe({
        next: (res) => {
          this.data = res.data;
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
  }

  isPrimitive(value: any): boolean {
    return value === null || value === undefined || typeof value !== 'object';
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isObject(value: any): boolean {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  isEmpty(value: any): boolean {
    if (Array.isArray(value)) return value.length === 0;
    if (this.isObject(value)) return Object.keys(value).length === 0;
    return value === null || value === undefined || value === '';
  }

  entries(value: any): ProfileEntry[] {
    if (!this.isObject(value)) return [];
    return Object.entries(value)
      .filter(([, v]) => !this.isEmpty(v))
      .map(([key, v]) => ({ key, value: v }));
  }

  learningProgressEntries(): ProfileEntry[] {
    return this.entries(this.learningProgressWithoutMoodle(this.data?.learning_progress));
  }

  getMoodleCourses(): any[] {
    const moodle =
      this.data?.profile?.moodle ||
      this.data?.profile?.learning_history?.moodle ||
      this.data?.learning_progress?.learning_history?.moodle;
    const courses = moodle?.courses || moodle?.enrollments || [];
    if (Array.isArray(courses)) return courses;
    if (this.isObject(courses)) return Object.values(courses);
    return [];
  }

  getMoodleCourseName(course: any): string {
    return course?.course_name || course?.fullname || course?.shortname || course?.course_shortname || '—';
  }

  getMoodleCourseShortname(course: any): string {
    return course?.shortname || course?.course_shortname || '—';
  }

  getMoodleCourseCategory(course: any): string {
    const category = course?.category_name || course?.category || course?.category_id;
    return category === null || category === undefined || category === '' ? '—' : String(category);
  }

  getMoodleCourseStatus(course: any): string {
    if (course?.status) return course.status;
    if (course?.role) return course.role;
    if (course?.completed !== undefined) {
      return course.completed === true || course.completed === 'true'
        ? 'abgeschlossen'
        : 'offen';
    }
    return '—';
  }

  getMoodleCourseProgress(course: any): string {
    return moodleCourseProgressText(course, this.t.instant('pages.dozent.participants.profile.progressCompleted'));
  }

  getMoodleCourseGrade(course: any): string {
    if (course?.grade_formatted) return course.grade_formatted;
    if (course?.grades?.length) {
      return course.grades
        .map((g: any) => g.grade_formatted || g.raw_grade)
        .filter(Boolean)
        .join(', ');
    }
    return '—';
  }

  private learningHistoryWithoutMoodle(value: any): any {
    if (!this.isObject(value)) return value;
    const rest = { ...value };
    delete rest.moodle;
    return Object.keys(rest).length > 0 ? rest : null;
  }

  private learningProgressWithoutMoodle(value: any): any {
    if (!this.isObject(value)) return value;
    const rest = { ...value };
    if (this.isObject(rest.learning_history)) {
      const learningHistory = this.learningHistoryWithoutMoodle(rest.learning_history);
      if (this.isEmpty(learningHistory)) {
        delete rest.learning_history;
      } else {
        rest.learning_history = learningHistory;
      }
    }
    return Object.keys(rest).length > 0 ? rest : null;
  }

  formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }

  formatPrimitive(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  }

  observationTitle(obs: any, index: number): string {
    const topic = this.formatPrimitive(obs?.topic).trim();
    if (topic && topic !== '—') return topic;

    const type = this.formatPrimitive(obs?.type).trim();
    if (type && type !== '—') return this.formatKey(type);

    return `Beobachtung ${index + 1}`;
  }

  observationPreview(obs: any): string {
    const content = this.formatPrimitive(obs?.content).trim();
    if (!content || content === '—') return '';
    return content.length > 120 ? `${content.slice(0, 117).trim()}...` : content;
  }

  observationRows(): ObservationRow[] {
    return this.filteredObservations().map((observation, index) => ({
      index,
      observation,
    }));
  }

  filteredObservations(): any[] {
    const observations = this.data?.observations || [];
    const query = this.normalize(this.observationFilter);
    if (!query) return observations;
    return observations.filter((obs: any, index: number) =>
      this.normalize(
        [
          obs?.type,
          obs?.topic,
          obs?.content,
          obs?.confidence,
          obs?.source_session,
          obs?.source,
          this.observationTitle(obs, index),
          this.observationDate(obs),
        ].join(' ')
      ).includes(query)
    );
  }

  filteredMoodleCourses(): any[] {
    const courses = this.getMoodleCourses();
    const query = this.normalize(this.moodleCourseFilter);
    if (!query) return courses;
    return courses.filter((course: any) =>
      this.normalize(
        [
          this.getMoodleCourseName(course),
          this.getMoodleCourseShortname(course),
          this.getMoodleCourseCategory(course),
          this.getMoodleCourseStatus(course),
          this.getMoodleCourseProgress(course),
          this.getMoodleCourseGrade(course),
        ].join(' ')
      ).includes(query)
    );
  }

  summaryText(): string {
    const summary = this.data?.consolidated_summary;
    if (typeof summary === 'string') return summary;
    if (this.isObject(summary) && summary.summary) return String(summary.summary);
    return '';
  }

  observationDate(obs: any): any {
    return obs?.created_at || obs?.createdAt || obs?.timestamp || null;
  }

  observationMetaEntries(obs: any): ProfileEntry[] {
    if (!this.isObject(obs)) return [];
    const hiddenKeys = new Set(['content']);
    return Object.entries(obs)
      .filter(([key, value]) => !hiddenKeys.has(key) && !this.isEmpty(value))
      .map(([key, value]) => ({ key, value }));
  }

  toggleObservation(index: number): void {
    this.expandedObservation = this.expandedObservation === index ? null : index;
  }

  private normalize(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  close() {
    this.dialogRef.close();
  }
}
